import { addOrUpdateGeoJsonSource, removeSourceAndLayers } from '../utils/maplibre-helpers';

const SOURCE_ID = 'src-parcel-measurements';
const LAYER_IDS = [
    'lyr-parcel-measure-house-fill',
    'lyr-parcel-measure-house-line',
    'lyr-parcel-measure-road-selection',
    'lyr-parcel-measure-inset',
    'lyr-parcel-measure-leader',
    'lyr-parcel-measure-endpoint',
    'lyr-parcel-measure-label',
    'lyr-parcel-measure-house-label',
    'lyr-parcel-measure-house-violation',
];
const EARTH_METERS_PER_DEGREE = 111320;
const SHORT_EDGE_METERS = 8;

function feature(geometry, properties) {
    return { type: 'Feature', geometry: geometry, properties: properties || {} };
}

function formatMeters(value) {
    const rounded = Math.round(value * 10) / 10;
    const formatted = rounded.toLocaleString('pl-PL', { maximumFractionDigits: 1 });
    if (!Number.isInteger(rounded)) return formatted + ' metra';
    const absolute = Math.abs(rounded);
    const lastTwo = absolute % 100;
    const last = absolute % 10;
    if (absolute === 1) return formatted + ' metr';
    if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return formatted + ' metry';
    return formatted + ' metrów';
}

function formatArea(value) {
    return Math.round(value).toLocaleString('pl-PL') + ' metrów kwadratowych';
}

function selectedRoadEdgesLabel(count) {
    if (!count) return 'Nie wskazano jeszcze krawędzi od strony drogi.';
    if (count === 1) return 'Wybrano jedną krawędź od strony drogi.';
    const lastTwo = count % 100;
    const last = count % 10;
    const noun = last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)
        ? 'krawędzie'
        : 'krawędzi';
    return 'Wybrano ' + count + ' ' + noun + ' od strony drogi.';
}

function polygonRings(geometry) {
    if (!geometry) return [];
    if (geometry.type === 'Polygon') return geometry.coordinates.length ? [geometry.coordinates[0]] : [];
    if (geometry.type === 'MultiPolygon') {
        return geometry.coordinates
            .filter((polygon) => Array.isArray(polygon) && polygon.length)
            .map((polygon) => polygon[0]);
    }
    return [];
}

function cleanRing(ring) {
    if (!Array.isArray(ring)) return [];
    const result = ring.filter((point) => Array.isArray(point) && point.length >= 2);
    if (result.length > 1) {
        const first = result[0];
        const last = result[result.length - 1];
        if (first[0] === last[0] && first[1] === last[1]) result.pop();
    }
    return result;
}

function projectionForRing(ring) {
    const origin = ring.reduce((sum, point) => {
        sum[0] += point[0];
        sum[1] += point[1];
        return sum;
    }, [0, 0]).map((value) => value / Math.max(1, ring.length));
    const metersPerLng = Math.max(1, EARTH_METERS_PER_DEGREE * Math.cos(origin[1] * Math.PI / 180));

    return {
        project: (point) => [
            (point[0] - origin[0]) * metersPerLng,
            (point[1] - origin[1]) * EARTH_METERS_PER_DEGREE,
        ],
        unproject: (point) => [
            origin[0] + point[0] / metersPerLng,
            origin[1] + point[1] / EARTH_METERS_PER_DEGREE,
        ],
    };
}

function signedArea(points) {
    let twiceArea = 0;
    for (let index = 0; index < points.length; index += 1) {
        const next = points[(index + 1) % points.length];
        twiceArea += points[index][0] * next[1] - next[0] * points[index][1];
    }
    return twiceArea / 2;
}

function distance(a, b) {
    return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function lineIntersection(first, second) {
    const cross = first.direction[0] * second.direction[1] - first.direction[1] * second.direction[0];
    if (Math.abs(cross) < 0.000001) return null;
    const delta = [second.point[0] - first.point[0], second.point[1] - first.point[1]];
    const scale = (delta[0] * second.direction[1] - delta[1] * second.direction[0]) / cross;
    return [
        first.point[0] + first.direction[0] * scale,
        first.point[1] + first.direction[1] * scale,
    ];
}

export function buildOffsetRing(ring, distances) {
    return buildOffsetSegments(ring, distances).map((segment) => segment[0]);
}

function logicalRingEdges(points, distances) {
    let edges = points.map((point, index) => ({
        start: point,
        end: points[(index + 1) % points.length],
        sourceIndexes: [index],
        inset: distances ? Math.max(0, Number(distances[index]) || 0) : 0,
    }));
    let changed = true;
    while (changed && edges.length > 3) {
        changed = false;
        for (let index = 0; index < edges.length; index += 1) {
            const nextIndex = (index + 1) % edges.length;
            const first = edges[index];
            const second = edges[nextIndex];
            if (Math.abs(first.inset - second.inset) > 0.01) continue;
            const firstLength = distance(first.start, first.end);
            const secondLength = distance(second.start, second.end);
            const firstDirection = [
                (first.end[0] - first.start[0]) / Math.max(0.0001, firstLength),
                (first.end[1] - first.start[1]) / Math.max(0.0001, firstLength),
            ];
            const secondDirection = [
                (second.end[0] - second.start[0]) / Math.max(0.0001, secondLength),
                (second.end[1] - second.start[1]) / Math.max(0.0001, secondLength),
            ];
            const dot = Math.max(-1, Math.min(1,
                firstDirection[0] * secondDirection[0] + firstDirection[1] * secondDirection[1]
            ));
            const turnDegrees = Math.acos(dot) * 180 / Math.PI;
            // Cadastral data often contains sub-metre vertices that only
            // approximate one straight boundary. Treat them as one logical
            // edge so their setback cannot become a long crossing segment.
            const isMicroKink = Math.min(firstLength, secondLength) < 1.5 && turnDegrees < 24;
            const isNearlyStraight = turnDegrees < 2.5;
            if (!isMicroKink && !isNearlyStraight) continue;
            const merged = {
                start: first.start,
                end: second.end,
                sourceIndexes: first.sourceIndexes.concat(second.sourceIndexes),
                inset: Math.max(first.inset, second.inset),
            };
            if (nextIndex === 0) {
                edges[0] = merged;
                edges.splice(index, 1);
            } else {
                edges[index] = merged;
                edges.splice(nextIndex, 1);
            }
            changed = true;
            break;
        }
    }
    return edges;
}

function isConvexRing(points) {
    let direction = 0;
    for (let index = 0; index < points.length; index += 1) {
        const a = points[index];
        const b = points[(index + 1) % points.length];
        const c = points[(index + 2) % points.length];
        const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
        if (Math.abs(cross) < 0.000001) continue;
        const nextDirection = cross > 0 ? 1 : -1;
        if (direction && direction !== nextDirection) return false;
        direction = nextDirection;
    }
    return direction !== 0;
}

function clipPolygonToOffsetLine(polygon, line) {
    if (!polygon.length) return [];
    const result = [];
    const signedDistance = (point) => (
        (point[0] - line.point[0]) * line.normal[0]
        + (point[1] - line.point[1]) * line.normal[1]
    );
    for (let index = 0; index < polygon.length; index += 1) {
        const start = polygon[index];
        const end = polygon[(index + 1) % polygon.length];
        const startDistance = signedDistance(start);
        const endDistance = signedDistance(end);
        const startInside = startDistance >= -0.000001;
        const endInside = endDistance >= -0.000001;
        if (startInside) result.push(start);
        if (startInside === endInside) continue;
        const amount = startDistance / (startDistance - endDistance);
        result.push([
            start[0] + (end[0] - start[0]) * amount,
            start[1] + (end[1] - start[1]) * amount,
        ]);
    }
    return result.filter((point, index) => (
        index === 0 || distance(point, result[index - 1]) > 0.000001
    ));
}

function convexOffsetSegments(points, shiftedLines, projection) {
    let clipped = points.slice();
    shiftedLines.forEach((line) => {
        clipped = clipPolygonToOffsetLine(clipped, line);
    });
    if (clipped.length > 1 && distance(clipped[0], clipped[clipped.length - 1]) < 0.000001) {
        clipped.pop();
    }
    if (clipped.length < 3 || Math.abs(signedArea(clipped)) < 0.000001) return [];
    return clipped.map((point, index) => {
        const next = clipped[(index + 1) % clipped.length];
        const center = midpoint(point, next);
        const owner = shiftedLines.reduce((best, line) => {
            const lineDistance = Math.abs(
                (center[0] - line.point[0]) * line.normal[0]
                + (center[1] - line.point[1]) * line.normal[1]
            );
            return !best || lineDistance < best.distance
                ? { line: line, distance: lineDistance }
                : best;
        }, null).line;
        const segment = [projection.unproject(point), projection.unproject(next)];
        segment.sourceIndexes = owner.sourceIndexes;
        return segment;
    });
}

export function buildOffsetSegments(ring, distances) {
    const coordinates = cleanRing(ring);
    if (coordinates.length < 3) return [];
    const projection = projectionForRing(coordinates);
    const points = coordinates.map(projection.project);
    const orientation = signedArea(points) >= 0 ? 1 : -1;
    const logicalEdges = logicalRingEdges(points, distances);
    const shiftedLines = logicalEdges.map((edge) => {
        const dx = edge.end[0] - edge.start[0];
        const dy = edge.end[1] - edge.start[1];
        const edgeLength = Math.max(0.0001, Math.hypot(dx, dy));
        const normal = orientation > 0
            ? [-dy / edgeLength, dx / edgeLength]
            : [dy / edgeLength, -dx / edgeLength];
        const inset = edge.inset;
        return {
            point: [edge.start[0] + normal[0] * inset, edge.start[1] + normal[1] * inset],
            end: [edge.end[0] + normal[0] * inset, edge.end[1] + normal[1] * inset],
            direction: [dx / edgeLength, dy / edgeLength],
            normal: normal,
            inset: inset,
            length: edgeLength,
            sourceIndexes: edge.sourceIndexes,
        };
    });
    if (isConvexRing(points)) {
        return convexOffsetSegments(points, shiftedLines, projection);
    }

    const joins = shiftedLines.map((_line, index) => {
        const previous = shiftedLines[(index - 1 + shiftedLines.length) % shiftedLines.length];
        const current = shiftedLines[index];
        const intersection = lineIntersection(previous, current);
        if (!intersection) return null;

        // An almost collinear cadastral "sliver" must never turn into a long
        // spike after offsetting. Accept a miter only when it stays close to
        // both shifted segment endpoints. Otherwise each segment keeps its
        // own shifted endpoint (a safe bevel/gap).
        const joinLimit = Math.min(
            Math.max(2, previous.inset, current.inset) * 2.5,
            Math.max(1.25, Math.min(previous.length, current.length) * 0.75 + 1)
        );
        if (distance(previous.end, intersection) > joinLimit
            || distance(current.point, intersection) > joinLimit) {
            return null;
        }
        return intersection;
    });

    return shiftedLines.map((line, index) => {
        const startJoin = joins[index];
        const endJoin = joins[(index + 1) % joins.length];
        const segment = [
            projection.unproject(startJoin || line.point),
            projection.unproject(endJoin || line.end),
        ];
        segment.sourceIndexes = line.sourceIndexes;
        return segment;
    });
}

function roadEdgeKeySet(options) {
    const values = Array.isArray(options.roadEdgeKeys)
        ? options.roadEdgeKeys
        : (options.roadEdgeKey == null ? [] : [options.roadEdgeKey]);
    return new Set(values.map(String));
}

export function measureRing(ring) {
    const coordinates = cleanRing(ring);
    if (coordinates.length < 3) return { areaM2: 0, edges: [] };
    const projection = projectionForRing(coordinates);
    const points = coordinates.map(projection.project);
    return {
        areaM2: Math.abs(signedArea(points)),
        edges: points.map((point, index) => ({
            index: index,
            lengthM: distance(point, points[(index + 1) % points.length]),
        })),
    };
}

export function parcelSimplificationSummary(geometry) {
    const summary = { simplified: false, originalEdges: 0, displayedEdges: 0, removedEdges: 0 };
    polygonRings(geometry).map(cleanRing).filter((ring) => ring.length >= 3).forEach((ring) => {
        const projection = projectionForRing(ring);
        summary.originalEdges += ring.length;
        summary.displayedEdges += logicalRingEdges(ring.map(projection.project)).length;
    });
    summary.removedEdges = Math.max(0, summary.originalEdges - summary.displayedEdges);
    summary.simplified = summary.removedEdges > 0;
    return summary;
}

function midpoint(a, b) {
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function pointAlongNormal(point, normal, meters, projection) {
    const projected = projection.project(point);
    return projection.unproject([
        projected[0] + normal[0] * meters,
        projected[1] + normal[1] * meters,
    ]);
}

function angleForLabel(a, b, projection) {
    const pa = projection.project(a);
    const pb = projection.project(b);
    let degrees = Math.atan2(pb[1] - pa[1], pb[0] - pa[0]) * 180 / Math.PI;
    if (degrees > 90) degrees -= 180;
    if (degrees < -90) degrees += 180;
    return degrees;
}

function addMeasurementLabel(features, a, b, label, kind, edgeKey, projection, labelNormal, forceLeader, displacementMeters) {
    const center = midpoint(a, b);
    const lengthM = distance(projection.project(a), projection.project(b));
    let labelPoint = center;
    if (forceLeader || (kind === 'edge-label' && lengthM < SHORT_EDGE_METERS)) {
        const displacement = displacementMeters != null
            ? displacementMeters
            : Math.max(6, Math.min(13, 9 - lengthM * 0.25));
        labelPoint = pointAlongNormal(center, labelNormal, displacement, projection);
        features.push(feature({
            type: 'LineString',
            coordinates: [center, labelPoint],
        }, { kind: 'leader', edgeKey: edgeKey }));
        features.push(feature({
            type: 'Point',
            coordinates: center,
        }, {
            kind: 'endpoint',
            edgeKey: edgeKey,
            rotation: angleForLabel(labelPoint, center, projection),
        }));
    }
    features.push(feature({ type: 'Point', coordinates: labelPoint }, {
        kind: kind,
        edgeKey: edgeKey,
        label: label,
        rotation: angleForLabel(a, b, projection),
    }));
}

function shortEdgeLabelLanes(lengths) {
    const lanes = lengths.map(() => 0);
    if (!lengths.length) return lanes;
    let start = lengths.findIndex((length) => length >= SHORT_EDGE_METERS);
    start = start < 0 ? 0 : (start + 1) % lengths.length;
    let lane = 0;
    let previousWasShort = false;
    for (let step = 0; step < lengths.length; step += 1) {
        const index = (start + step) % lengths.length;
        if (lengths[index] < SHORT_EDGE_METERS) {
            lane = previousWasShort ? lane + 1 : 0;
            lanes[index] = lane;
            previousWasShort = true;
        } else {
            lane = 0;
            previousWasShort = false;
        }
    }
    return lanes;
}

function pointInPolygon(point, polygon) {
    let inside = false;
    for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index, index += 1) {
        const a = polygon[index];
        const b = polygon[previous];
        const crosses = ((a[1] > point[1]) !== (b[1] > point[1]))
            && (point[0] < (b[0] - a[0]) * (point[1] - a[1]) / ((b[1] - a[1]) || 0.0000001) + a[0]);
        if (crosses) inside = !inside;
    }
    return inside;
}

function pointSegmentDistance(point, a, b) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lengthSquared = dx * dx + dy * dy;
    const amount = lengthSquared
        ? Math.max(0, Math.min(1, ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / lengthSquared))
        : 0;
    return Math.hypot(point[0] - (a[0] + amount * dx), point[1] - (a[1] + amount * dy));
}

function rectangleBoundaryPoints(center, width, depth, angleDegrees, samplesPerSide) {
    const angle = angleDegrees * Math.PI / 180;
    const ux = [Math.cos(angle), Math.sin(angle)];
    const uy = [-Math.sin(angle), Math.cos(angle)];
    const points = [];
    samplesPerSide = Math.max(2, Number(samplesPerSide) || 5);
    for (let side = 0; side < 4; side += 1) {
        for (let sample = 0; sample < samplesPerSide; sample += 1) {
            const amount = sample / (samplesPerSide - 1);
            let alongX;
            let alongY;
            if (side === 0) { alongX = -width / 2 + width * amount; alongY = -depth / 2; }
            else if (side === 1) { alongX = width / 2; alongY = -depth / 2 + depth * amount; }
            else if (side === 2) { alongX = width / 2 - width * amount; alongY = depth / 2; }
            else { alongX = -width / 2; alongY = depth / 2 - depth * amount; }
            points.push([
                center[0] + ux[0] * alongX + uy[0] * alongY,
                center[1] + ux[1] * alongX + uy[1] * alongY,
            ]);
        }
    }
    return points;
}

function rectangleCorners(center, width, depth, angleDegrees) {
    const boundary = rectangleBoundaryPoints(center, width, depth, angleDegrees);
    return [boundary[0], boundary[4], boundary[9], boundary[14], boundary[0]];
}

function normalizeHouseContext(geometry, options) {
    const rings = polygonRings(geometry).map(cleanRing).filter((ring) => ring.length >= 3);
    if (!rings.length) return null;
    let globalOffset = 0;
    const candidates = rings.map((ring) => {
        const result = { ring: ring, area: measureRing(ring).areaM2, globalOffset: globalOffset };
        globalOffset += ring.length;
        return result;
    }).sort((a, b) => b.area - a.area);
    const selected = candidates[0];
    const projection = projectionForRing(selected.ring);
    const polygon = selected.ring.map(projection.project);
    const roadKeys = roadEdgeKeySet(options);
    const edges = polygon.map((point, index) => ({
        a: point,
        b: polygon[(index + 1) % polygon.length],
        setback: roadKeys.has(String(selected.globalOffset + index))
            ? Math.max(0, Number(options.roadSetbackM) || 0)
            : Math.max(0, Number(options.boundarySetbackM) || 0),
    }));
    const bounds = polygon.reduce((result, point) => ({
        minX: Math.min(result.minX, point[0]),
        minY: Math.min(result.minY, point[1]),
        maxX: Math.max(result.maxX, point[0]),
        maxY: Math.max(result.maxY, point[1]),
    }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
    return { projection: projection, polygon: polygon, edges: edges, bounds: bounds };
}

function pointClearsSetbacks(point, context) {
    if (!pointInPolygon(point, context.polygon)) return false;
    return context.edges.every((edge) => (
        pointSegmentDistance(point, edge.a, edge.b) + 0.02 >= edge.setback
    ));
}

function rectangleFits(center, width, depth, rotation, context) {
    return rectangleBoundaryPoints(center, width, depth, rotation)
        .every((point) => pointClearsSetbacks(point, context));
}

function candidateHouseCenters(context, step) {
    const centers = [];
    const center = [
        (context.bounds.minX + context.bounds.maxX) / 2,
        (context.bounds.minY + context.bounds.maxY) / 2,
    ];
    for (let y = context.bounds.minY; y <= context.bounds.maxY; y += step) {
        for (let x = context.bounds.minX; x <= context.bounds.maxX; x += step) {
            const point = [x, y];
            if (pointClearsSetbacks(point, context)) centers.push(point);
        }
    }
    centers.push(center);
    centers.sort((a, b) => distance(a, center) - distance(b, center));
    return centers;
}

export function findHouseRectangle(geometry, options) {
    options = options || {};
    const width = Math.max(1, Number(options.widthM) || 10);
    const depth = Math.max(1, Number(options.depthM) || 10);
    const rotation = Number(options.rotationDeg) || 0;
    const context = normalizeHouseContext(geometry, options);
    if (!context) return null;
    const centers = options.candidateCenters || candidateHouseCenters(context, 0.75);
    const center = centers.find((candidate) => rectangleFits(candidate, width, depth, rotation, context));
    if (!center) return null;
    return {
        center: context.projection.unproject(center),
        coordinates: rectangleCorners(center, width, depth, rotation).map(context.projection.unproject),
        widthM: width,
        depthM: depth,
        rotationDeg: rotation,
        areaM2: width * depth,
    };
}

function houseRectangleAtCenter(geometry, options, centerLngLat) {
    const context = normalizeHouseContext(geometry, options || {});
    if (!context || !centerLngLat) return null;
    const width = Math.max(1, Number(options.widthM) || 10);
    const depth = Math.max(1, Number(options.depthM) || 10);
    const rotation = Number(options.rotationDeg) || 0;
    const center = context.projection.project(centerLngLat);
    return {
        center: centerLngLat.slice(),
        coordinates: rectangleCorners(center, width, depth, rotation).map(context.projection.unproject),
        widthM: width,
        depthM: depth,
        rotationDeg: rotation,
        areaM2: width * depth,
    };
}

function houseRectangleFitsPlacement(geometry, options, placement) {
    const context = normalizeHouseContext(geometry, options || {});
    if (!context || !placement || !placement.center) return false;
    return rectangleFits(
        context.projection.project(placement.center),
        placement.widthM,
        placement.depthM,
        placement.rotationDeg,
        context
    );
}

function houseViolationPoints(geometry, options, placement) {
    const context = normalizeHouseContext(geometry, options || {});
    if (!context || !placement || !placement.center) return [];
    const center = context.projection.project(placement.center);
    return rectangleBoundaryPoints(
        center,
        placement.widthM,
        placement.depthM,
        placement.rotationDeg,
        17
    ).filter((point) => !pointClearsSetbacks(point, context))
        .map(context.projection.unproject);
}

function defaultHouseCenter(geometry, options) {
    const context = normalizeHouseContext(geometry, options || {});
    if (!context) return null;
    const candidates = candidateHouseCenters(context, 1);
    const center = candidates[0] || [
        (context.bounds.minX + context.bounds.maxX) / 2,
        (context.bounds.minY + context.bounds.maxY) / 2,
    ];
    return context.projection.unproject(center);
}

export function findHouseRectangleAnyRotation(geometry, options) {
    options = options || {};
    const context = normalizeHouseContext(geometry, options);
    if (!context) return null;
    const width = Math.max(1, Number(options.widthM) || 10);
    const depth = Math.max(1, Number(options.depthM) || 10);
    const currentRotation = ((Number(options.rotationDeg) || 0) % 180 + 180) % 180;
    const centers = candidateHouseCenters(context, 1);
    if (!centers.length) return null;
    const edgeAngles = context.edges.map((edge) => (
        Math.atan2(edge.b[1] - edge.a[1], edge.b[0] - edge.a[0]) * 180 / Math.PI
    ));
    const angles = Array.from(new Set(
        [currentRotation].concat(edgeAngles, Array.from({ length: 36 }, (_value, index) => index * 5))
            .map((angle) => Math.round((((angle % 180) + 180) % 180) * 10) / 10)
    )).sort((first, second) => {
        const distanceFromCurrent = (angle) => {
            const difference = Math.abs(angle - currentRotation);
            return Math.min(difference, 180 - difference);
        };
        return distanceFromCurrent(first) - distanceFromCurrent(second);
    });
    for (let angleIndex = 0; angleIndex < angles.length; angleIndex += 1) {
        const rotation = angles[angleIndex];
        const center = centers.find((candidate) => rectangleFits(candidate, width, depth, rotation, context));
        if (!center) continue;
        return {
            center: context.projection.unproject(center),
            coordinates: rectangleCorners(center, width, depth, rotation).map(context.projection.unproject),
            widthM: width,
            depthM: depth,
            rotationDeg: rotation,
            areaM2: width * depth,
        };
    }
    return null;
}

export function findLargestHouseRectangle(geometry, options) {
    options = options || {};
    const context = normalizeHouseContext(geometry, options);
    if (!context) return null;
    const baseWidth = Math.max(1, Number(options.widthM) || 10);
    const baseDepth = Math.max(1, Number(options.depthM) || 10);
    const centers = candidateHouseCenters(context, 1);
    if (!centers.length) return null;

    const edgeAngles = context.edges.map((edge) => (
        Math.atan2(edge.b[1] - edge.a[1], edge.b[0] - edge.a[0]) * 180 / Math.PI
    ));
    const angles = Array.from(new Set([0, 90].concat(edgeAngles).map((angle) => (
        Math.round(((angle % 180) + 180) % 180 * 10) / 10
    ))));
    let best = null;
    const diagonal = Math.hypot(
        context.bounds.maxX - context.bounds.minX,
        context.bounds.maxY - context.bounds.minY
    );
    const maximumScale = Math.max(1, diagonal / Math.min(baseWidth, baseDepth));

    angles.forEach((rotation) => {
        let low = 0;
        let high = maximumScale;
        let placement = null;
        for (let attempt = 0; attempt < 11; attempt += 1) {
            const scale = (low + high) / 2;
            const width = baseWidth * scale;
            const depth = baseDepth * scale;
            const center = centers.find((candidate) => rectangleFits(candidate, width, depth, rotation, context));
            if (center) {
                low = scale;
                placement = { center: center, width: width, depth: depth };
            } else {
                high = scale;
            }
        }
        if (!placement || (best && placement.width * placement.depth <= best.areaM2)) return;
        best = {
            center: context.projection.unproject(placement.center),
            coordinates: rectangleCorners(placement.center, placement.width, placement.depth, rotation)
                .map(context.projection.unproject),
            widthM: placement.width,
            depthM: placement.depth,
            rotationDeg: rotation,
            areaM2: placement.width * placement.depth,
        };
    });
    return best;
}

export function buildParcelMeasurementFeatures(geometry, options) {
    options = options || {};
    const rings = polygonRings(geometry).map(cleanRing).filter((ring) => ring.length >= 3);
    const features = [];
    const roadKeys = roadEdgeKeySet(options);
    let globalEdgeIndex = 0;

    rings.forEach((ring, ringIndex) => {
        const projection = projectionForRing(ring);
        const projected = ring.map(projection.project);
        const orientation = signedArea(projected) >= 0 ? 1 : -1;
        const logicalOuterEdges = logicalRingEdges(projected);
        const outerLengths = logicalOuterEdges.map((edge) => distance(edge.start, edge.end));
        const outerLabelLanes = shortEdgeLabelLanes(outerLengths);
        ring.forEach((point, edgeIndex) => {
            const next = ring[(edgeIndex + 1) % ring.length];
            const edgeKey = String(globalEdgeIndex + edgeIndex);
            if (roadKeys.has(edgeKey)) {
                features.push(feature({
                    type: 'LineString',
                    coordinates: [point, next],
                }, {
                    kind: 'road-selection',
                    edgeKey: edgeKey,
                }));
            }
        });
        logicalOuterEdges.forEach((edge, edgeIndex) => {
            const point = projection.unproject(edge.start);
            const next = projection.unproject(edge.end);
            const edgeKey = String(globalEdgeIndex + edge.sourceIndexes[0]);
            const pa = edge.start;
            const pb = edge.end;
            const edgeLength = Math.max(0.0001, distance(pa, pb));
            const inwardNormal = orientation > 0
                ? [-(pb[1] - pa[1]) / edgeLength, (pb[0] - pa[0]) / edgeLength]
                : [(pb[1] - pa[1]) / edgeLength, -(pb[0] - pa[0]) / edgeLength];
            addMeasurementLabel(
                features,
                point,
                next,
                formatMeters(edgeLength),
                'edge-label',
                edgeKey,
                projection,
                [-inwardNormal[0], -inwardNormal[1]],
                false,
                edgeLength < SHORT_EDGE_METERS ? 3.5 + outerLabelLanes[edgeIndex] * 3.5 : null
            );
        });
        globalEdgeIndex += ring.length;

        if (options.setbacksEnabled) {
            const distances = ring.map((_point, edgeIndex) => {
                const isRoad = roadKeys.has(String(options.ringEdgeOffsets[ringIndex] + edgeIndex));
                return isRoad ? options.roadSetbackM : options.boundarySetbackM;
            });
            const offsetSegments = buildOffsetSegments(ring, distances);
            offsetSegments.forEach((segment) => {
                const point = segment[0];
                const next = segment[1];
                const sourceIndexes = segment.sourceIndexes || [0];
                const edgeKey = String(options.ringEdgeOffsets[ringIndex] + sourceIndexes[0]);
                const isRoad = sourceIndexes.some((sourceIndex) => (
                    roadKeys.has(String(options.ringEdgeOffsets[ringIndex] + sourceIndex))
                ));
                features.push(feature({
                    type: 'LineString',
                    coordinates: [point, next],
                }, {
                    kind: isRoad ? 'road-inset' : 'boundary-inset',
                    edgeKey: edgeKey,
                }));
                const pa = projection.project(point);
                const pb = projection.project(next);
                const edgeLength = distance(pa, pb);
                const dx = pb[0] - pa[0];
                const dy = pb[1] - pa[1];
                const inwardNormal = orientation > 0
                    ? [-dy / Math.max(0.0001, edgeLength), dx / Math.max(0.0001, edgeLength)]
                    : [dy / Math.max(0.0001, edgeLength), -dx / Math.max(0.0001, edgeLength)];
                addMeasurementLabel(
                    features,
                    point,
                    next,
                    formatMeters(edgeLength),
                    isRoad ? 'road-inset-label' : 'inset-label',
                    edgeKey,
                    projection,
                    inwardNormal,
                    false
                );
            });
        }
    });

    if (options.housePlacement) {
        const house = options.housePlacement;
        const oppositeHandleEdgeCenter = midpoint(house.coordinates[0], house.coordinates[1]);
        features.push(feature({
            type: 'Polygon',
            coordinates: [house.coordinates],
        }, {
            kind: 'house',
            fits: options.houseFits !== false,
            widthM: house.widthM,
            depthM: house.depthM,
            areaM2: house.areaM2,
        }));
        features.push(feature({ type: 'Point', coordinates: oppositeHandleEdgeCenter }, {
            kind: 'house-label',
            fits: options.houseFits !== false,
            label: 'Prostokąt domu\n' + formatMeters(house.widthM) + ' × ' + formatMeters(house.depthM),
        }));
        (options.houseViolationPoints || []).forEach((point) => {
            features.push(feature({ type: 'Point', coordinates: point }, {
                kind: 'house-violation',
            }));
        });
    }

    return { type: 'FeatureCollection', features: features };
}

function ringEdgeOffsets(geometry) {
    const offsets = [];
    let count = 0;
    polygonRings(geometry).forEach((ring) => {
        offsets.push(count);
        count += cleanRing(ring).length;
    });
    return offsets;
}

export function createParcelMeasurements(config) {
    let parcelFeature = null;
    let visible = false;
    let setbacksEnabled = false;
    let roadSetbackM = 5;
    let boundarySetbackM = 4;
    let roadEdgeKeys = [];
    let choosingRoadEdge = false;
    let roadSelectionMessage = '';
    let houseEnabled = false;
    let houseWidthM = 10;
    let houseDepthM = 10;
    let houseRotationDeg = 0;
    let housePlacement = null;
    let houseCenter = null;
    let houseFits = false;
    let fittedHousePlacement = null;
    let houseStatus = '';
    let houseStatusState = '';
    let rotationHandleMarker = null;
    let rotationRenderFrame = null;
    let suppressMapClickUntil = 0;
    let rotationHandleDragging = false;
    let dragHousePlacement = null;
    let dragHouseFits = false;
    let houseMoving = false;
    let houseMoveStartLngLat = null;
    let houseMoveStartCenter = null;
    let restoreMapDragPan = false;
    let simplificationSummary = parcelSimplificationSummary(null);
    let pendingStyleRender = false;

    function notifyStateChange() {
        if (typeof config.onStateChange === 'function') {
            config.onStateChange(getUrlState());
        }
    }

    function getUrlState() {
        return {
            visible: visible,
            setbacksEnabled: setbacksEnabled,
        };
    }

    function setUrlState(state) {
        visible = !!(state && state.visible);
        setbacksEnabled = !!(state && state.setbacksEnabled);
    }

    function map() {
        return config.getMap();
    }

    function clearMap() {
        const currentMap = map();
        if (currentMap) removeSourceAndLayers(currentMap, SOURCE_ID, LAYER_IDS);
    }

    function removeRotationHandle() {
        if (!rotationHandleMarker) return;
        rotationHandleMarker.remove();
        rotationHandleMarker = null;
    }

    function normalizedRotation(value) {
        return ((Number(value) || 0) % 180 + 180) % 180;
    }

    function rotationFromHandle(lngLat, center) {
        const metersPerLng = Math.max(
            1,
            EARTH_METERS_PER_DEGREE * Math.cos(center[1] * Math.PI / 180)
        );
        const dx = (lngLat.lng - center[0]) * metersPerLng;
        const dy = (lngLat.lat - center[1]) * EARTH_METERS_PER_DEGREE;
        // Uchwyt leży pośrodku boku wyznaczającego głębokość, czyli 90
        // stopni względem osi szerokości prostokąta.
        return normalizedRotation(Math.atan2(dy, dx) * 180 / Math.PI - 90);
    }

    function updateHouseControls() {
        const root = config.detailsContentEl && config.detailsContentEl.querySelector('[data-parcel-measurements]');
        if (!root) return;
        const widthInput = root.querySelector('[data-parcel-house-width]');
        const depthInput = root.querySelector('[data-parcel-house-depth]');
        const rotationInput = root.querySelector('[data-parcel-house-rotation]');
        const rotationOutput = root.querySelector('[data-parcel-house-rotation-output]');
        const preview = root.querySelector('[data-parcel-house-preview-shape]');
        const previewWidth = root.querySelector('[data-parcel-house-preview-width]');
        const previewDepth = root.querySelector('[data-parcel-house-preview-depth]');
        if (widthInput && document.activeElement !== widthInput) widthInput.value = houseWidthM;
        if (depthInput && document.activeElement !== depthInput) depthInput.value = houseDepthM;
        if (rotationInput && document.activeElement !== rotationInput) rotationInput.value = houseRotationDeg;
        if (rotationOutput) rotationOutput.textContent = Math.round(houseRotationDeg) + ' stopni';
        if (previewWidth) previewWidth.textContent = formatMeters(houseWidthM);
        if (previewDepth) previewDepth.textContent = formatMeters(houseDepthM);
        if (preview) {
            const largest = Math.max(houseWidthM, houseDepthM, 1);
            preview.style.width = Math.max(34, houseWidthM / largest * 76) + 'px';
            preview.style.height = Math.max(34, houseDepthM / largest * 76) + 'px';
        }
    }

    function currentHouseOptions() {
        return {
            widthM: houseWidthM,
            depthM: houseDepthM,
            rotationDeg: houseRotationDeg,
            roadSetbackM: roadSetbackM,
            boundarySetbackM: boundarySetbackM,
            roadEdgeKeys: roadEdgeKeys,
        };
    }

    function previewInteractiveHouse(placement, fits) {
        dragHousePlacement = placement;
        dragHouseFits = fits;
        houseStatusState = fits ? 'success' : 'invalid';
        houseStatus = fits
            ? 'Prostokąt zachowuje wybrane odległości od granic.'
            : 'Prostokąt nie zachowuje wybranych odległości od granic. Czerwone oznaczenia pokazują miejsca naruszenia.';
        if (rotationHandleMarker) {
            rotationHandleMarker.getElement().classList.toggle('is-invalid', !fits);
        }
        updateControls();
        if (rotationRenderFrame != null) cancelAnimationFrame(rotationRenderFrame);
        rotationRenderFrame = requestAnimationFrame(() => {
            rotationRenderFrame = null;
            const previewMap = map();
            if (!previewMap || !dragHousePlacement || !previewMap.getSource(SOURCE_ID)) return;
            const options = currentHouseOptions();
            const data = buildParcelMeasurementFeatures(parcelFeature.geometry, {
                setbacksEnabled: setbacksEnabled,
                roadSetbackM: roadSetbackM,
                boundarySetbackM: boundarySetbackM,
                roadEdgeKeys: roadEdgeKeys,
                ringEdgeOffsets: ringEdgeOffsets(parcelFeature.geometry),
                housePlacement: dragHousePlacement,
                houseFits: dragHouseFits,
                houseViolationPoints: dragHouseFits ? [] : houseViolationPoints(
                    parcelFeature.geometry,
                    options,
                    dragHousePlacement
                ),
            });
            addOrUpdateGeoJsonSource(previewMap, SOURCE_ID, data);
        });
    }

    function syncRotationHandle(currentMap) {
        if (!housePlacement || !visible || !houseEnabled || !setbacksEnabled
            || typeof maplibregl === 'undefined') {
            removeRotationHandle();
            return;
        }
        const topEdgeCenter = midpoint(housePlacement.coordinates[2], housePlacement.coordinates[3]);
        if (!rotationHandleMarker) {
            const element = document.createElement('button');
            element.type = 'button';
            element.className = 'nm-parcel-house-rotate-handle';
            element.title = 'Przeciągnij, aby obrócić prostokąt domu';
            element.setAttribute('aria-label', 'Przeciągnij, aby obrócić prostokąt domu');
            element.innerHTML = '<span aria-hidden="true">↻</span>';
            ['mousedown', 'pointerdown', 'touchstart'].forEach((eventName) => {
                element.addEventListener(eventName, () => {
                    suppressMapClickUntil = Date.now() + 5000;
                });
            });
            ['click', 'dblclick', 'contextmenu'].forEach((eventName) => {
                element.addEventListener(eventName, (event) => {
                    suppressMapClickUntil = Date.now() + 600;
                    event.preventDefault();
                    event.stopPropagation();
                });
            });
            rotationHandleMarker = new maplibregl.Marker({
                element: element,
                draggable: true,
                anchor: 'center',
            }).setLngLat(topEdgeCenter).addTo(currentMap);
            rotationHandleMarker.on('dragstart', () => {
                suppressMapClickUntil = Date.now() + 5000;
                rotationHandleDragging = true;
                houseCenter = housePlacement.center.slice();
            });
            rotationHandleMarker.on('drag', () => {
                const angle = rotationFromHandle(rotationHandleMarker.getLngLat(), housePlacement.center);
                houseRotationDeg = Math.round(angle * 10) / 10;
                fittedHousePlacement = null;
                const dragOptions = {
                    widthM: houseWidthM,
                    depthM: houseDepthM,
                    rotationDeg: houseRotationDeg,
                    roadSetbackM: roadSetbackM,
                    boundarySetbackM: boundarySetbackM,
                    roadEdgeKeys: roadEdgeKeys,
                };
                dragHousePlacement = houseRectangleAtCenter(
                    parcelFeature.geometry,
                    dragOptions,
                    houseCenter
                );
                dragHouseFits = houseRectangleFitsPlacement(
                    parcelFeature.geometry,
                    dragOptions,
                    dragHousePlacement
                );
                previewInteractiveHouse(dragHousePlacement, dragHouseFits);
            });
            rotationHandleMarker.on('dragend', () => {
                suppressMapClickUntil = Date.now() + 600;
                houseRotationDeg = Math.round(
                    rotationFromHandle(rotationHandleMarker.getLngLat(), housePlacement.center) * 10
                ) / 10;
                rotationHandleDragging = false;
                dragHousePlacement = null;
                dragHouseFits = false;
                fittedHousePlacement = null;
                updateHouseControls();
                render();
            });
        } else if (!rotationHandleDragging) {
            rotationHandleMarker.setLngLat(topEdgeCenter);
        }
        rotationHandleMarker.getElement().classList.toggle('is-invalid', !houseFits);
    }

    function updateControls() {
        const root = config.detailsContentEl && config.detailsContentEl.querySelector('[data-parcel-measurements]');
        if (!root) return;
        const toggle = root.querySelector('[data-parcel-measure-toggle]');
        const settings = root.querySelector('[data-parcel-measure-settings]');
        const setbacks = root.querySelector('[data-parcel-setbacks]');
        const roadStatus = root.querySelector('[data-parcel-road-status]');
        const roadButton = root.querySelector('[data-parcel-road-pick]');
        const roadActions = root.querySelector('[data-parcel-road-actions]');
        const roadCount = root.querySelector('[data-parcel-road-count]');
        const roadConfirm = root.querySelector('[data-parcel-road-confirm]');
        const house = root.querySelector('[data-parcel-house]');
        const houseStatusEl = root.querySelector('[data-parcel-house-status]');
        const simplificationNotice = root.querySelector('[data-parcel-simplification-notice]');
        const simplificationCount = root.querySelector('[data-parcel-simplification-count]');
        if (toggle) {
            toggle.setAttribute('aria-pressed', visible ? 'true' : 'false');
            toggle.classList.toggle('is-active', visible);
            const label = toggle.querySelector('[data-parcel-measure-toggle-label]');
            if (label) label.textContent = visible ? 'Ukryj wymiary na mapie' : 'Pokaż wymiary na mapie';
        }
        if (settings) settings.hidden = !visible;
        if (setbacks) setbacks.hidden = !setbacksEnabled;
        if (roadStatus) {
            roadStatus.textContent = roadSelectionMessage || selectedRoadEdgesLabel(roadEdgeKeys.length);
        }
        if (roadButton) {
            roadButton.hidden = choosingRoadEdge;
            roadButton.classList.remove('is-active');
        }
        if (roadActions) roadActions.hidden = !choosingRoadEdge;
        if (roadCount) roadCount.textContent = String(roadEdgeKeys.length);
        if (roadConfirm) roadConfirm.disabled = roadEdgeKeys.length === 0;
        if (house) house.hidden = !houseEnabled;
        if (houseStatusEl) {
            houseStatusEl.textContent = houseStatus;
            houseStatusEl.dataset.state = houseStatusState;
        }
        if (simplificationNotice) simplificationNotice.hidden = !simplificationSummary.simplified;
        if (simplificationCount) {
            const count = simplificationSummary.removedEdges;
            const lastTwo = count % 100;
            const last = count % 10;
            const plural = last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14);
            simplificationCount.textContent = count === 1
                ? 'Połączono jedną krótką lub niemal współliniową krawędź.'
                : 'Połączono ' + count + (plural
                    ? ' krótkie lub niemal współliniowe krawędzie.'
                    : ' krótkich lub niemal współliniowych krawędzi.');
        }
        updateHouseControls();
    }

    function render() {
        clearMap();
        const currentMap = map();
        if (!visible || !parcelFeature || !parcelFeature.geometry || !currentMap) {
            removeRotationHandle();
            updateControls();
            return;
        }
        if (!currentMap.isStyleLoaded()) {
            removeRotationHandle();
            updateControls();
            if (!pendingStyleRender) {
                pendingStyleRender = true;
                currentMap.once('idle', () => {
                    pendingStyleRender = false;
                    render();
                });
            }
            return;
        }
        pendingStyleRender = false;

        housePlacement = null;
        houseFits = false;
        houseStatus = '';
        houseStatusState = '';
        if (setbacksEnabled && houseEnabled) {
            const houseOptions = {
                widthM: houseWidthM,
                depthM: houseDepthM,
                rotationDeg: houseRotationDeg,
                roadSetbackM: roadSetbackM,
                boundarySetbackM: boundarySetbackM,
                roadEdgeKeys: roadEdgeKeys,
            };
            if (fittedHousePlacement) {
                housePlacement = fittedHousePlacement;
                houseCenter = fittedHousePlacement.center.slice();
                houseFits = true;
            } else if (houseCenter) {
                housePlacement = houseRectangleAtCenter(parcelFeature.geometry, houseOptions, houseCenter);
                houseFits = houseRectangleFitsPlacement(parcelFeature.geometry, houseOptions, housePlacement);
            } else {
                housePlacement = findHouseRectangle(parcelFeature.geometry, houseOptions);
                houseFits = !!housePlacement;
                if (housePlacement) {
                    houseCenter = housePlacement.center.slice();
                } else {
                    houseCenter = defaultHouseCenter(parcelFeature.geometry, houseOptions);
                    housePlacement = houseRectangleAtCenter(parcelFeature.geometry, houseOptions, houseCenter);
                }
            }
            if (houseFits) {
                houseStatus = 'Prostokąt zachowuje wybrane odległości od granic. Powierzchnia zabudowy: '
                    + formatArea(housePlacement.areaM2) + '.';
                houseStatusState = 'success';
            } else {
                houseStatus = 'Prostokąt nie zachowuje wybranych odległości od granic. Czerwone oznaczenia pokazują miejsca naruszenia. Użyj przycisku „Znajdź miejsce”.';
                houseStatusState = 'invalid';
            }
        }
        updateControls();

        const data = buildParcelMeasurementFeatures(parcelFeature.geometry, {
            setbacksEnabled: setbacksEnabled,
            roadSetbackM: roadSetbackM,
            boundarySetbackM: boundarySetbackM,
            roadEdgeKeys: roadEdgeKeys,
            ringEdgeOffsets: ringEdgeOffsets(parcelFeature.geometry),
            housePlacement: housePlacement,
            houseFits: houseFits,
            houseViolationPoints: houseFits ? [] : houseViolationPoints(parcelFeature.geometry, {
                roadSetbackM: roadSetbackM,
                boundarySetbackM: boundarySetbackM,
                roadEdgeKeys: roadEdgeKeys,
            }, housePlacement),
        });
        addOrUpdateGeoJsonSource(currentMap, SOURCE_ID, data);
        currentMap.addLayer({
            id: LAYER_IDS[0],
            type: 'fill',
            source: SOURCE_ID,
            filter: ['==', ['get', 'kind'], 'house'],
            paint: {
                'fill-color': ['case', ['get', 'fits'], '#2563eb', '#ef4444'],
                'fill-opacity': ['case', ['get', 'fits'], 0.25, 0.2],
            },
        });
        currentMap.addLayer({
            id: LAYER_IDS[1],
            type: 'line',
            source: SOURCE_ID,
            filter: ['==', ['get', 'kind'], 'house'],
            paint: {
                'line-color': ['case', ['get', 'fits'], '#1d4ed8', '#dc2626'],
                'line-width': 3,
            },
        });
        currentMap.addLayer({
            id: LAYER_IDS[2],
            type: 'line',
            source: SOURCE_ID,
            filter: ['==', ['get', 'kind'], 'road-selection'],
            paint: {
                'line-color': '#ef4444',
                'line-width': 7,
                'line-opacity': choosingRoadEdge ? 0.9 : 0.62,
                'line-blur': 0.5,
            },
        });
        currentMap.addLayer({
            id: LAYER_IDS[3],
            type: 'line',
            source: SOURCE_ID,
            filter: ['in', ['get', 'kind'], ['literal', ['road-inset', 'boundary-inset']]],
            paint: {
                'line-color': ['match', ['get', 'kind'], 'road-inset', '#dc2626', '#0f766e'],
                'line-width': 2.4,
                'line-dasharray': [2, 1.4],
            },
        });
        currentMap.addLayer({
            id: LAYER_IDS[4],
            type: 'line',
            source: SOURCE_ID,
            filter: ['==', ['get', 'kind'], 'leader'],
            paint: { 'line-color': '#111827', 'line-width': 1.2, 'line-opacity': 0.85 },
        });
        currentMap.addLayer({
            id: LAYER_IDS[5],
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['==', ['get', 'kind'], 'endpoint'],
            layout: {
                'text-field': '▸',
                'text-size': 13,
                'text-rotate': ['get', 'rotation'],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
            },
            paint: { 'text-color': '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 1 },
        });
        currentMap.addLayer({
            id: LAYER_IDS[6],
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['in', ['get', 'kind'], ['literal', ['edge-label', 'inset-label', 'road-inset-label']]],
            layout: {
                'text-field': ['get', 'label'],
                'text-size': ['match', ['get', 'kind'], 'edge-label', 11, 10],
                'text-rotate': ['get', 'rotation'],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                'text-padding': 1,
            },
            paint: {
                'text-color': ['match', ['get', 'kind'], 'road-inset-label', '#991b1b', 'inset-label', '#115e59', '#7c2d12'],
                'text-halo-color': '#ffffff',
                'text-halo-width': 2,
            },
        });
        currentMap.addLayer({
            id: LAYER_IDS[7],
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['==', ['get', 'kind'], 'house-label'],
            layout: {
                'text-field': ['get', 'label'],
                'text-size': 11,
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                'text-line-height': 1.25,
            },
            paint: {
                'text-color': ['case', ['get', 'fits'], '#1e3a8a', '#991b1b'],
                'text-halo-color': ['case', ['get', 'fits'], '#eff6ff', '#fef2f2'],
                'text-halo-width': 3,
            },
        });
        currentMap.addLayer({
            id: LAYER_IDS[8],
            type: 'circle',
            source: SOURCE_ID,
            filter: ['==', ['get', 'kind'], 'house-violation'],
            paint: {
                'circle-radius': 4.5,
                'circle-color': '#dc2626',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1.5,
                'circle-opacity': 0.95,
            },
        });
        syncRotationHandle(currentMap);
    }

    function setFeature(nextFeature) {
        const currentId = parcelFeature && parcelFeature.properties
            ? parcelFeature.properties.id_dzialki || parcelFeature.properties.id
            : null;
        const nextId = nextFeature && nextFeature.properties
            ? nextFeature.properties.id_dzialki || nextFeature.properties.id
            : null;
        if (currentId != null && nextId != null && String(currentId) !== String(nextId)) {
            visible = false;
            setbacksEnabled = false;
            roadEdgeKeys = [];
            choosingRoadEdge = false;
            roadSelectionMessage = '';
            houseEnabled = false;
            fittedHousePlacement = null;
            houseCenter = null;
        }
        parcelFeature = nextFeature || null;
        simplificationSummary = parcelSimplificationSummary(parcelFeature && parcelFeature.geometry);
        render();
    }

    function clear() {
        parcelFeature = null;
        visible = false;
        setbacksEnabled = false;
        roadEdgeKeys = [];
        choosingRoadEdge = false;
        roadSelectionMessage = '';
        houseEnabled = false;
        housePlacement = null;
        houseCenter = null;
        houseFits = false;
        fittedHousePlacement = null;
        houseStatus = '';
        houseStatusState = '';
        rotationHandleDragging = false;
        dragHousePlacement = null;
        dragHouseFits = false;
        houseMoving = false;
        houseMoveStartLngLat = null;
        houseMoveStartCenter = null;
        simplificationSummary = parcelSimplificationSummary(null);
        const currentMap = map();
        if (restoreMapDragPan && currentMap && currentMap.dragPan && currentMap.dragPan.enable) {
            currentMap.dragPan.enable();
        }
        restoreMapDragPan = false;
        if (currentMap && currentMap.getCanvas()) currentMap.getCanvas().style.cursor = '';
        clearMap();
        removeRotationHandle();
        updateControls();
    }

    function nearestEdgeKey(event) {
        if (!parcelFeature || !parcelFeature.geometry || !event || !event.point) return null;
        const currentMap = map();
        let nearest = null;
        let globalIndex = 0;
        polygonRings(parcelFeature.geometry).forEach((rawRing) => {
            const ring = cleanRing(rawRing);
            ring.forEach((point, index) => {
                const next = ring[(index + 1) % ring.length];
                const a = currentMap.project(point);
                const b = currentMap.project(next);
                const dx = b.x - a.x;
                const dy = b.y - a.y;
                const lengthSquared = dx * dx + dy * dy;
                const amount = lengthSquared
                    ? Math.max(0, Math.min(1, ((event.point.x - a.x) * dx + (event.point.y - a.y) * dy) / lengthSquared))
                    : 0;
                const x = a.x + amount * dx;
                const y = a.y + amount * dy;
                const pixelDistance = Math.hypot(event.point.x - x, event.point.y - y);
                if (!nearest || pixelDistance < nearest.distance) {
                    nearest = { key: String(globalIndex), distance: pixelDistance };
                }
                globalIndex += 1;
            });
        });
        // Sixty pixels creates a forgiving touch/click corridor around the
        // cadastral line without changing the visible geometry.
        return nearest && nearest.distance <= 60 ? nearest.key : null;
    }

    function expandRoadEdgeKeys(edgeKey) {
        const wanted = Number(edgeKey);
        if (!Number.isInteger(wanted) || !parcelFeature || !parcelFeature.geometry) return [String(edgeKey)];
        let offset = 0;
        const rings = polygonRings(parcelFeature.geometry).map(cleanRing);
        for (let ringIndex = 0; ringIndex < rings.length; ringIndex += 1) {
            const ring = rings[ringIndex];
            if (wanted < offset || wanted >= offset + ring.length) {
                offset += ring.length;
                continue;
            }
            const projection = projectionForRing(ring);
            const points = ring.map(projection.project);
            const directions = points.map((point, index) => {
                const next = points[(index + 1) % points.length];
                const length = Math.max(0.0001, distance(point, next));
                return [(next[0] - point[0]) / length, (next[1] - point[1]) / length];
            });
            const local = wanted - offset;
            const selected = new Set([local]);
            const similar = (first, second) => {
                const dot = Math.max(-1, Math.min(1,
                    directions[first][0] * directions[second][0]
                    + directions[first][1] * directions[second][1]
                ));
                return Math.acos(dot) * 180 / Math.PI <= 15;
            };
            let current = local;
            while (selected.size < ring.length) {
                const previous = (current - 1 + ring.length) % ring.length;
                if (selected.has(previous) || !similar(previous, current)) break;
                selected.add(previous);
                current = previous;
            }
            current = local;
            while (selected.size < ring.length) {
                const next = (current + 1) % ring.length;
                if (selected.has(next) || !similar(current, next)) break;
                selected.add(next);
                current = next;
            }
            return Array.from(selected).sort((a, b) => a - b).map((index) => String(offset + index));
        }
        return [String(edgeKey)];
    }

    function handleMapClick(event) {
        if (Date.now() < suppressMapClickUntil) return true;
        if (!choosingRoadEdge) return false;
        const edgeKey = nearestEdgeKey(event);
        if (edgeKey == null) {
            roadSelectionMessage = 'Kliknij bliżej granicy działki. Aktywny obszar wyboru jest szerszy niż widoczna linia.';
            updateControls();
            return true;
        }
        const group = expandRoadEdgeKeys(edgeKey);
        const selected = new Set(roadEdgeKeys.map(String));
        const removeGroup = group.every((key) => selected.has(String(key)));
        group.forEach((key) => {
            if (removeGroup) selected.delete(String(key));
            else selected.add(String(key));
        });
        roadEdgeKeys = Array.from(selected).sort((a, b) => Number(a) - Number(b));
        roadSelectionMessage = roadEdgeKeys.length === 0
            ? 'Usunięto zaznaczenie. Kliknij jedną lub kilka krawędzi działki.'
            : selectedRoadEdgesLabel(roadEdgeKeys.length) + ' Klikaj dalej albo zatwierdź wybór.';
        fittedHousePlacement = null;
        render();
        return true;
    }

    function houseLayerHit(event) {
        const currentMap = map();
        if (!currentMap || !event || !event.point || !currentMap.getLayer(LAYER_IDS[0])) return false;
        try {
            return currentMap.queryRenderedFeatures(event.point, { layers: [LAYER_IDS[0]] }).length > 0;
        } catch (_error) {
            return false;
        }
    }

    function handleMapPointerDown(event) {
        const originalTarget = event && event.originalEvent && event.originalEvent.target;
        if (originalTarget && originalTarget.closest
            && originalTarget.closest('.nm-parcel-house-rotate-handle')) {
            return false;
        }
        if (!visible || !setbacksEnabled || !houseEnabled || !housePlacement
            || rotationHandleDragging || !houseLayerHit(event)) {
            return false;
        }
        const currentMap = map();
        houseMoving = true;
        suppressMapClickUntil = Date.now() + 5000;
        fittedHousePlacement = null;
        houseCenter = housePlacement.center.slice();
        houseMoveStartCenter = houseCenter.slice();
        houseMoveStartLngLat = {
            lng: event.lngLat.lng,
            lat: event.lngLat.lat,
        };
        restoreMapDragPan = !!(currentMap.dragPan
            && currentMap.dragPan.isEnabled
            && currentMap.dragPan.isEnabled());
        if (currentMap.dragPan && currentMap.dragPan.disable) currentMap.dragPan.disable();
        if (event.preventDefault) event.preventDefault();
        if (currentMap.getCanvas()) currentMap.getCanvas().style.cursor = 'grabbing';
        const originalType = event.originalEvent && event.originalEvent.type;
        document.addEventListener(
            originalType && originalType.indexOf('touch') === 0 ? 'touchend' : 'mouseup',
            handleMapPointerUp,
            { once: true }
        );
        return true;
    }

    function handleMapMove(event) {
        const currentMap = map();
        if (houseMoving) {
            if (!event || !event.lngLat || !houseMoveStartLngLat || !houseMoveStartCenter) return true;
            houseCenter = [
                houseMoveStartCenter[0] + event.lngLat.lng - houseMoveStartLngLat.lng,
                houseMoveStartCenter[1] + event.lngLat.lat - houseMoveStartLngLat.lat,
            ];
            const options = currentHouseOptions();
            const placement = houseRectangleAtCenter(parcelFeature.geometry, options, houseCenter);
            const fits = houseRectangleFitsPlacement(parcelFeature.geometry, options, placement);
            housePlacement = placement;
            previewInteractiveHouse(placement, fits);
            if (rotationHandleMarker && placement) {
                rotationHandleMarker.setLngLat(midpoint(
                    placement.coordinates[2],
                    placement.coordinates[3]
                ));
            }
            if (currentMap && currentMap.getCanvas()) currentMap.getCanvas().style.cursor = 'grabbing';
            return true;
        }
        if (!choosingRoadEdge) {
            if (!currentMap || !currentMap.getCanvas()) return false;
            if (visible && setbacksEnabled && houseEnabled && houseLayerHit(event)) {
                currentMap.getCanvas().style.cursor = 'move';
                return true;
            }
            if (currentMap.getCanvas().style.cursor === 'move'
                || currentMap.getCanvas().style.cursor === 'grabbing') {
                currentMap.getCanvas().style.cursor = '';
            }
            return false;
        }
        if (!currentMap || !currentMap.getCanvas()) return false;
        currentMap.getCanvas().style.cursor = nearestEdgeKey(event) == null ? 'crosshair' : 'pointer';
        return true;
    }

    function handleMapPointerUp() {
        if (!houseMoving) return false;
        const currentMap = map();
        houseMoving = false;
        suppressMapClickUntil = Date.now() + 600;
        fittedHousePlacement = null;
        if (dragHousePlacement) {
            houseCenter = dragHousePlacement.center.slice();
            housePlacement = dragHousePlacement;
        }
        dragHousePlacement = null;
        dragHouseFits = false;
        houseMoveStartLngLat = null;
        houseMoveStartCenter = null;
        if (restoreMapDragPan && currentMap && currentMap.dragPan && currentMap.dragPan.enable) {
            currentMap.dragPan.enable();
        }
        restoreMapDragPan = false;
        if (currentMap && currentMap.getCanvas()) currentMap.getCanvas().style.cursor = 'move';
        render();
        return true;
    }

    function finishRoadSelection() {
        choosingRoadEdge = false;
        roadSelectionMessage = '';
        const currentMap = map();
        if (currentMap && currentMap.getCanvas()) currentMap.getCanvas().style.cursor = '';
        render();
    }

    function handleDetailClick(event) {
        const toggle = event.target.closest('[data-parcel-measure-toggle]');
        if (toggle) {
            event.preventDefault();
            visible = !visible;
            if (!visible) {
                choosingRoadEdge = false;
                roadSelectionMessage = '';
                const currentMap = map();
                if (currentMap && currentMap.getCanvas()) currentMap.getCanvas().style.cursor = '';
            }
            if (typeof config.onVisibilityChange === 'function') {
                config.onVisibilityChange(visible, parcelFeature);
            }
            notifyStateChange();
            render();
            return true;
        }
        const roadPick = event.target.closest('[data-parcel-road-pick]');
        if (roadPick) {
            event.preventDefault();
            choosingRoadEdge = true;
            roadSelectionMessage = roadEdgeKeys.length
                ? 'Obecne zaznaczenie pozostaje aktywne. Klikaj krawędzie, aby je dodać albo odznaczyć.'
                : 'Klikaj kolejno krawędzie od strony drogi. Wybrane odcinki podświetlą się na czerwono.';
            const currentMap = map();
            if (currentMap && currentMap.getCanvas()) currentMap.getCanvas().style.cursor = 'crosshair';
            updateControls();
            return true;
        }
        const roadConfirm = event.target.closest('[data-parcel-road-confirm]');
        if (roadConfirm) {
            event.preventDefault();
            if (roadEdgeKeys.length) finishRoadSelection();
            return true;
        }
        const roadClear = event.target.closest('[data-parcel-road-clear]');
        if (roadClear) {
            event.preventDefault();
            roadEdgeKeys = [];
            fittedHousePlacement = null;
            roadSelectionMessage = 'Wyczyszczono wybór. Kliknij jedną lub kilka krawędzi działki.';
            render();
            return true;
        }
        const findHouse = event.target.closest('[data-parcel-house-find]');
        if (findHouse) {
            event.preventDefault();
            if (!parcelFeature || !parcelFeature.geometry) return true;
            const found = findHouseRectangleAnyRotation(parcelFeature.geometry, {
                widthM: houseWidthM,
                depthM: houseDepthM,
                rotationDeg: houseRotationDeg,
                roadSetbackM: roadSetbackM,
                boundarySetbackM: boundarySetbackM,
                roadEdgeKeys: roadEdgeKeys,
            });
            if (found) {
                fittedHousePlacement = found;
                houseCenter = found.center.slice();
                houseRotationDeg = Math.round(found.rotationDeg * 10) / 10;
                render();
            } else {
                fittedHousePlacement = null;
                render();
                houseStatus = 'Nie znaleziono miejsca dla prostokąta o tych wymiarach przy żadnym sprawdzonym kącie. Zmniejsz szerokość lub głębokość albo zmień wymagane odległości.';
                houseStatusState = 'invalid';
                updateControls();
            }
            return true;
        }
        const fitHouse = event.target.closest('[data-parcel-house-fit]');
        if (fitHouse) {
            event.preventDefault();
            if (!parcelFeature || !parcelFeature.geometry) return true;
            const largest = findLargestHouseRectangle(parcelFeature.geometry, {
                widthM: houseWidthM,
                depthM: houseDepthM,
                roadSetbackM: roadSetbackM,
                boundarySetbackM: boundarySetbackM,
                roadEdgeKeys: roadEdgeKeys,
            });
            if (largest) {
                fittedHousePlacement = largest;
                houseCenter = largest.center.slice();
                houseWidthM = Math.round(largest.widthM * 10) / 10;
                houseDepthM = Math.round(largest.depthM * 10) / 10;
                houseRotationDeg = Math.round(largest.rotationDeg * 10) / 10;
                const root = config.detailsContentEl.querySelector('[data-parcel-measurements]');
                if (root) {
                    const widthInput = root.querySelector('[data-parcel-house-width]');
                    const depthInput = root.querySelector('[data-parcel-house-depth]');
                    const rotationInput = root.querySelector('[data-parcel-house-rotation]');
                    if (widthInput) widthInput.value = houseWidthM;
                    if (depthInput) depthInput.value = houseDepthM;
                    if (rotationInput) rotationInput.value = houseRotationDeg;
                }
            } else {
                houseStatus = 'Nie udało się znaleźć prostokąta z zachowaniem wybranych odległości od granic.';
            }
            render();
            return true;
        }
        return false;
    }

    function handleDetailChange(event) {
        const target = event.target;
        if (!target) return false;
        if (target.matches('[data-parcel-setbacks-toggle]')) {
            setbacksEnabled = !!target.checked;
            fittedHousePlacement = null;
            if (!setbacksEnabled) {
                choosingRoadEdge = false;
                roadSelectionMessage = '';
                const currentMap = map();
                if (currentMap && currentMap.getCanvas()) currentMap.getCanvas().style.cursor = '';
            }
            notifyStateChange();
            render();
            return true;
        }
        if (target.matches('[data-parcel-road-setback]')) {
            roadSetbackM = Math.max(0, Math.min(100, Number(target.value) || 0));
            fittedHousePlacement = null;
            render();
            return true;
        }
        if (target.matches('[data-parcel-boundary-setback]')) {
            boundarySetbackM = Math.max(0, Math.min(100, Number(target.value) || 0));
            fittedHousePlacement = null;
            render();
            return true;
        }
        if (target.matches('[data-parcel-house-toggle]')) {
            houseEnabled = !!target.checked;
            if (!houseEnabled) fittedHousePlacement = null;
            render();
            return true;
        }
        if (target.matches('[data-parcel-house-width]')) {
            houseWidthM = Math.max(1, Math.min(200, Number(target.value) || 1));
            fittedHousePlacement = null;
            render();
            return true;
        }
        if (target.matches('[data-parcel-house-depth]')) {
            houseDepthM = Math.max(1, Math.min(200, Number(target.value) || 1));
            fittedHousePlacement = null;
            render();
            return true;
        }
        if (target.matches('[data-parcel-house-rotation]')) {
            houseRotationDeg = normalizedRotation(target.value);
            fittedHousePlacement = null;
            render();
            return true;
        }
        return false;
    }

    function handleDetailInput(event) {
        const target = event.target;
        if (!target || !target.matches('[data-parcel-house-rotation]')) return false;
        houseRotationDeg = normalizedRotation(target.value);
        fittedHousePlacement = null;
        updateHouseControls();
        if (rotationRenderFrame != null) cancelAnimationFrame(rotationRenderFrame);
        rotationRenderFrame = requestAnimationFrame(() => {
            rotationRenderFrame = null;
            render();
        });
        return true;
    }

    function controlsHtml(helpButton) {
        return '<section class="ms-card ms-card--grid nm-detail-block nm-parcel-measurements" data-parcel-measurements>'
            + '<div class="nm-parcel-measurements__head">'
            + '<div><div class="nm-section-label">Wymiary działki</div>'
            + '<p>Długość każdej krawędzi bezpośrednio na mapie.</p></div>'
            + helpButton
            + '</div>'
            + '<button type="button" class="nm-parcel-measurements__toggle" data-parcel-measure-toggle aria-pressed="' + (visible ? 'true' : 'false') + '">'
            + '<span class="nm-parcel-measurements__icon" aria-hidden="true">⌁</span>'
            + '<span data-parcel-measure-toggle-label>' + (visible ? 'Ukryj wymiary na mapie' : 'Pokaż wymiary na mapie') + '</span>'
            + '</button>'
            + '<div class="nm-parcel-measurements__settings" data-parcel-measure-settings' + (visible ? '' : ' hidden') + '>'
            + '<label class="nm-parcel-measurements__check"><input type="checkbox" data-parcel-setbacks-toggle' + (setbacksEnabled ? ' checked' : '') + '>'
            + '<span>Pokaż orientacyjne odległości zabudowy</span></label>'
            + '<div class="nm-parcel-measurements__setbacks" data-parcel-setbacks' + (setbacksEnabled ? '' : ' hidden') + '>'
            + '<div class="nm-parcel-measurements__fields">'
            + '<label><span>Od drogi</span><span class="nm-parcel-measurements__input"><input type="number" min="0" max="100" step="0.5" value="' + roadSetbackM + '" data-parcel-road-setback><b>metrów</b></span></label>'
            + '<label><span>Od pozostałych krawędzi</span><span class="nm-parcel-measurements__input"><input type="number" min="0" max="100" step="0.5" value="' + boundarySetbackM + '" data-parcel-boundary-setback><b>metry</b></span></label>'
            + '</div>'
            + '<button type="button" class="nm-parcel-measurements__road" data-parcel-road-pick>Wskaż krawędzie od strony drogi</button>'
            + '<div class="nm-parcel-measurements__road-actions" data-parcel-road-actions' + (choosingRoadEdge ? '' : ' hidden') + '>'
            + '<div class="nm-parcel-measurements__road-guide">'
            + '<strong>Wybierz kilka krawędzi</strong>'
            + '<span>Klikaj kolejno w pobliżu granicy działki. Ponowne kliknięcie zaznaczonego odcinka usuwa go z wyboru.</span>'
            + '</div>'
            + '<div class="nm-parcel-measurements__road-buttons">'
            + '<button type="button" data-parcel-road-clear>Wyczyść</button>'
            + '<button type="button" class="is-primary" data-parcel-road-confirm' + (roadEdgeKeys.length ? '' : ' disabled') + '>OK · <span data-parcel-road-count>' + roadEdgeKeys.length + '</span></button>'
            + '</div>'
            + '</div>'
            + '<small data-parcel-road-status>' + selectedRoadEdgesLabel(roadEdgeKeys.length) + '</small>'
            + '<aside class="nm-parcel-measurements__notice" data-parcel-simplification-notice' + (simplificationSummary.simplified ? '' : ' hidden') + '>'
            + '<strong>Uproszczony podgląd geometrii</strong>'
            + '<p><span data-parcel-simplification-count></span> Zabieg ogranicza błędne przecięcia linii odległości przy bardzo krótkich załamaniach granicy.</p>'
            + '<p>Wymiary, linie odległości i prostokąt domu są wyłącznie orientacyjną wizualizacją. Nie stanowią geodezyjnego wyznaczenia granic, potwierdzenia zgodności z przepisami ani podstawy projektu budowlanego. Granice, wymagane odległości i możliwość zabudowy potwierdź u uprawnionego geodety lub projektanta oraz we właściwym urzędzie.</p>'
            + '</aside>'
            + '<div class="nm-parcel-measurements__house-divider"></div>'
            + '<label class="nm-parcel-measurements__check"><input type="checkbox" data-parcel-house-toggle' + (houseEnabled ? ' checked' : '') + '>'
            + '<span>Umieść prostokąt domu z zachowaniem odległości</span></label>'
            + '<div class="nm-parcel-measurements__house" data-parcel-house' + (houseEnabled ? '' : ' hidden') + '>'
            + '<div class="nm-parcel-measurements__house-config">'
            + '<div class="nm-parcel-measurements__house-preview" aria-hidden="true">'
            + '<div class="nm-parcel-measurements__house-preview-shape" data-parcel-house-preview-shape>'
            + '<span class="is-width" data-parcel-house-preview-width>' + formatMeters(houseWidthM) + '</span>'
            + '<span class="is-depth" data-parcel-house-preview-depth>' + formatMeters(houseDepthM) + '</span>'
            + '</div></div>'
            + '<div class="nm-parcel-measurements__fields nm-parcel-measurements__fields--house">'
            + '<label><span>Szerokość domu</span><span class="nm-parcel-measurements__input"><input type="number" min="1" max="200" step="0.5" value="' + houseWidthM + '" data-parcel-house-width><b>metrów</b></span></label>'
            + '<label><span>Głębokość domu</span><span class="nm-parcel-measurements__input"><input type="number" min="1" max="200" step="0.5" value="' + houseDepthM + '" data-parcel-house-depth><b>metrów</b></span></label>'
            + '</div>'
            + '</div>'
            + '<label class="nm-parcel-measurements__rotation">'
            + '<span><strong>Obrót prostokąta</strong><output data-parcel-house-rotation-output>' + Math.round(houseRotationDeg) + ' stopni</output></span>'
            + '<input type="range" min="0" max="179" step="1" value="' + houseRotationDeg + '" data-parcel-house-rotation>'
            + '<small>Przesuń suwak albo przeciągnij niebieski uchwyt na prostokącie domu.</small>'
            + '</label>'
            + '<p class="nm-parcel-measurements__drag-hint"><span aria-hidden="true">✥</span> Przeciągnij wnętrze prostokąta na mapie, aby zmienić jego położenie.</p>'
            + '<div class="nm-parcel-measurements__house-actions">'
            + '<button type="button" class="nm-parcel-measurements__road nm-parcel-measurements__find" data-parcel-house-find>Znajdź miejsce</button>'
            + '<button type="button" class="nm-parcel-measurements__road nm-parcel-measurements__fit" data-parcel-house-fit>Dopasuj największy prostokąt</button>'
            + '</div>'
            + '<small class="nm-parcel-measurements__house-status" data-parcel-house-status data-state="' + houseStatusState + '">' + houseStatus + '</small>'
            + '</div>'
            + '</div></div></section>';
    }

    return {
        clear: clear,
        controlsHtml: controlsHtml,
        handleDetailChange: handleDetailChange,
        handleDetailClick: handleDetailClick,
        handleDetailInput: handleDetailInput,
        handleMapClick: handleMapClick,
        handleMapMove: handleMapMove,
        handleMapPointerDown: handleMapPointerDown,
        handleMapPointerUp: handleMapPointerUp,
        getUrlState: getUrlState,
        render: render,
        setFeature: setFeature,
        setUrlState: setUrlState,
    };
}
